import type {
  AuditEvent,
  AvailabilityWindowRecord,
  ReservationListItem as ContractItem,
} from '@ooh/contracts';
import type { AvailabilityWindowRow } from '../components/availability-window-list';
import type { UnitReservationRow } from '../components/unit-reservations-tab';
import {
  formatAuditAction,
  formatPolishDate,
  formatPolishDateTime,
  formatReservationSource,
  mapBaseReservationFields,
} from './formatters';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Search params for the "Otwórz pełną kolejkę" (CU-86cb7ctfw) deep link to
 * the global /reservations list, pre-scoped to this unit + term so the
 * operator lands on the exact row they came from, already expanded.
 */
export interface OpenFullQueueSearch {
  hasQueue: true;
  mediaUnitId: string[];
  dateFrom: string;
  dateTo: string;
  expand: number;
  status: string[];
  holderUserId: string[];
  offerId: string[];
  source: string[];
  clientId: string[];
  salespersonId: string[];
  limit: number;
  offset: number;
  sort: 'date_from';
  order: 'asc';
}

/**
 * Returns null when `reservationId` doesn't match any row in `unitRows`; the button
 * that triggers this is always rendered from that same array, so this is a
 * defensive no-op, not a reachable user path.
 */
export function buildOpenFullQueueSearch(
  unitId: number,
  unitRows: Array<{ id: number; dateFrom: string; dateTo: string }>,
  reservationId: number,
): OpenFullQueueSearch | null {
  const target = unitRows.find((r) => r.id === reservationId);
  if (!target) return null;
  return {
    hasQueue: true,
    mediaUnitId: [String(unitId)],
    dateFrom: target.dateFrom,
    dateTo: target.dateTo,
    expand: reservationId,
    status: [],
    holderUserId: [],
    offerId: [],
    source: [],
    clientId: [],
    salespersonId: [],
    limit: 50,
    offset: 0,
    sort: 'date_from',
    order: 'asc',
  };
}

export function makeUnitRowMapper(t: Translate): (item: ContractItem) => UnitReservationRow {
  return (item: ContractItem): UnitReservationRow => ({
    ...mapBaseReservationFields(
      item,
      t(`status.${item.status}`),
      formatReservationSource(item.source, t),
    ),
    clientName: item.clientName,
  });
}

export function makeAvailabilityWindowMapper(
  t: (key: string) => string,
): (row: AvailabilityWindowRecord) => AvailabilityWindowRow {
  return (row: AvailabilityWindowRecord): AvailabilityWindowRow => ({
    id: row.id,
    dateFrom: formatPolishDate(row.dateFrom),
    dateTo: formatPolishDate(row.dateTo),
    state: row.state,
    stateLabel: t(`availabilityWindow.state.${row.state}`),
  });
}

export function mapAuditEvent(event: AuditEvent, t: Translate) {
  return {
    id: event.id,
    action: event.action,
    actionLabel: formatAuditAction(event.action, t),
    actorUserName: event.actorUserName,
    source: event.source ?? '',
    sourceLabel: event.source ? formatReservationSource(event.source, t) : '',
    details: (event.details ?? {}) as Record<string, unknown>,
    createdAt: formatPolishDateTime(event.createdAt),
  };
}
